import { useCallback, useEffect, useState } from 'react';

// Sub-pixel scroll positions mean an "at the end" scrollport is rarely exact.
const EDGE_TOLERANCE = 1;

type UseScrollShadowsArgs = {
    disabled?: boolean;
};

/* Tracks whether a scrollport has content out of view above or below it, so the chrome
 * around it can show an edge only when there is more to reach.
 *
 * Derived from scroll position rather than from sentinel elements and an
 * IntersectionObserver: observer callbacks are not delivered at all in a throttled or
 * nested frame, which silently left the affordance stuck off.
 *
 * The element is held in state rather than a ref because a dialog's paper mounts into a
 * portal on a later commit than the component that owns this hook, so a ref read during
 * the first effect is still null and nothing would tell it the element arrived.
 */
export const useScrollShadows = ({ disabled = false }: UseScrollShadowsArgs) => {
    const [scrollport, setScrollport] = useState<HTMLDivElement | null>(null);
    const [showTopShadow, setShowTopShadow] = useState(false);
    const [showBottomShadow, setShowBottomShadow] = useState(false);

    const update = useCallback(() => {
        if (disabled || scrollport === null) {
            setShowTopShadow(false);
            setShowBottomShadow(false);
            return;
        }
        const scrollableHeight = scrollport.scrollHeight - scrollport.clientHeight;
        setShowTopShadow(scrollport.scrollTop > EDGE_TOLERANCE);
        setShowBottomShadow(
            scrollableHeight > EDGE_TOLERANCE &&
                scrollport.scrollTop < scrollableHeight - EDGE_TOLERANCE,
        );
    }, [disabled, scrollport]);

    useEffect(() => {
        if (disabled || scrollport === null) {
            update();
            return;
        }
        update();
        scrollport.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        // Body content that grows or shrinks changes what is out of view.
        const observer = new ResizeObserver(update);
        observer.observe(scrollport);
        [...scrollport.children].forEach((child) => observer.observe(child));
        return () => {
            scrollport.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
            observer.disconnect();
        };
    }, [disabled, scrollport, update]);

    return {
        bodyRef: setScrollport,
        showTopShadow,
        showBottomShadow,
    };
};
